// src/lib/http.js
import { api } from '../config';

const DEFAULT_TIMEOUT_MS =
  Number(process.env.REACT_APP_HTTP_TIMEOUT_MS) || 15000;

function withTimeout(promiseFactory, ms = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return Promise.race([
    promiseFactory(controller.signal),
    new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), ms)),
  ]).finally(() => clearTimeout(timer));
}

async function request(path, { method = 'GET', headers = {}, body, ...rest } = {}) {
  return withTimeout(async (signal) => {
    const isFormData = body instanceof FormData;
    const preparedBody =
      body == null
        ? undefined
        : isFormData
          ? body
          : typeof body === 'string'
            ? body
            : JSON.stringify(body);

    const res = await fetch(api.url(path), {
      method,
      credentials: 'include',
      signal,
      headers: {
        Accept: 'application/json',
        // Only set Content-Type if NOT FormData and we actually have a body
        ...(preparedBody && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: preparedBody,
      ...rest,
    });

    const ct = res.headers.get('content-type') || '';
    const parse = ct.includes('application/json') ? res.json.bind(res) : res.text.bind(res);

    let data = null;
    try {
      const parsed = await parse();
      // Make non-JSON text look similar to JSON responses
      data = typeof parsed === 'string' ? { message: parsed } : parsed;
    } catch {
      data = null;
    }

    if (!res.ok) {
      const message = data?.error || data?.message || `HTTP ${res.status} ${res.statusText}`;
      const err = new Error(message);
      err.status = res.status;
      err.data = data;
      throw err;
    }

    return data;
  });
}

export const http = {
  request,
  get: (p, opts) => request(p, { ...opts, method: 'GET' }),
  post: (p, body, opts) => request(p, { ...opts, method: 'POST', body }),
  put: (p, body, opts) => request(p, { ...opts, method: 'PUT', body }),
  patch: (p, body, opts) => request(p, { ...opts, method: 'PATCH', body }),
  del: (p, opts) => request(p, { ...opts, method: 'DELETE' }),
};
